/**
 * Centralised role-check helper.
 *
 * The role model is two roles, both returned by the backend in uppercase:
 * `ADMIN` (full access) and `CONSULTANT` (self-service). Direct comparisons
 * against lowercase string literals silently never match, which has caused
 * real bugs (hidden nav items, missing dashboard widgets). Always use
 * `hasRole` instead of comparing `user.role` directly.
 */

export type Role = "ADMIN" | "CONSULTANT";

export const hasRole = (user: { role?: string } | null | undefined, ...roles: Role[]): boolean => {
  if (!user?.role) {
    return false;
  }
  const normalized = user.role.toUpperCase() as Role;
  return roles.includes(normalized);
};

/**
 * Whether the "Consultants" directory entry (dashboard Quick Links / stat)
 * should be shown. It links to the admin consultants area, so only ADMIN gets
 * it (see issue #601); CONSULTANTs are routed to "My Engagements" instead.
 */
export const canViewConsultantsNav = (user: { role?: string } | null | undefined): boolean =>
  hasRole(user, "ADMIN");

/**
 * Minimum user shape needed by the display-name helpers. Kept structural so
 * both the `User` type from `AuthContext` and other user-shaped payloads
 * (e.g. dev-user fixtures, API responses) can be passed in without a cast.
 */
interface DisplayUser {
  email?: string;
  username?: string;
  full_name?: string | null;
  consultant_profile?: { full_name?: string } | null;
}

/**
 * Resolve a user's display name from the fields the backend returns.
 *
 * Fallback order (must stay in sync with `getUserInitials`):
 *   1. `consultant_profile.full_name` — canonical, richest form
 *   2. `user.full_name` — set for admin/standard users without a profile
 *   3. `user.email` — safe last-resort identifier
 *   4. `user.username` — unlikely, but keeps something legible
 *   5. `"User"` — never render an empty greeting
 *
 * Historically this lived in two places (`Navbar.tsx` and `DashboardPage.tsx`)
 * and the copies drifted: `Navbar` was missing the `full_name` fallback, so
 * the sidebar showed an admin's raw email while the greeting on the same
 * page was correct (#699). Same class of bug as the role-check duplication
 * in #601 — consolidating the fallback chain here keeps every call site in
 * lockstep.
 */
export const getUserDisplayName = (user: DisplayUser | null | undefined): string => {
  if (!user) {
    return "User";
  }
  return (
    user.consultant_profile?.full_name || user.full_name || user.email || user.username || "User"
  );
};

/**
 * Two-letter avatar initials derived from `getUserDisplayName(user)`.
 *
 * Uses the first letter of the first two whitespace-separated parts when
 * the display name looks like a full name; otherwise falls back to the
 * first two characters of the display string (e.g. an email). Always
 * upper-cased so "martin bonde" and "Martin Bonde" render identically.
 */
export const getUserInitials = (user: DisplayUser | null | undefined): string => {
  const name = getUserDisplayName(user);
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};
